import { useQueries, useQuery } from '@tanstack/react-query'
import { configQueryOptions } from '~/domains/configuration/queries/config'
import type { ConfigName } from '~/domains/configuration/types'
import { oauthClientActivationQueryOptions } from '../oauth/queries/oauthClientActivationQuery'
import type { ExtensionDefinition, ExtensionState } from '../types'

/**
 * Reads the configuration keys of a single extension. Keys are plain `useConfig` cache entries, so
 * several extensions rendered together still travel in one request — the configuration batcher
 * groups the reads of a same tick — and a write through `useUpdateConfig` refreshes them on its own.
 *
 * An OAuth partner reads its activation instead. An extension with neither has no activation to
 * report: its state is `null`.
 */
export const useExtensionState = (
  extension: ExtensionDefinition
): {
  state: ExtensionState | null
  isLoading: boolean
  isError: boolean
} => {
  const { enabledConfig, oauthClientId, requiredConfigKeys = [] } = extension
  const oauthActivation = useQuery({
    ...oauthClientActivationQueryOptions(oauthClientId ?? ''),
    enabled: oauthClientId !== undefined
  })
  const configNames: ConfigName[] = [
    ...(enabledConfig === undefined ? [] : [enabledConfig]),
    ...requiredConfigKeys
  ]

  const results = useQueries({
    queries: configNames.map((name) => configQueryOptions(name))
  })

  const isLoading = results.some((result) => result.isLoading)
  const isError = results.some((result) => result.isError)

  if (oauthClientId !== undefined) {
    return {
      state: {
        isEnabled: oauthActivation.data?.active === true,
        isConfigured: true
      },
      isLoading: oauthActivation.isLoading,
      isError: oauthActivation.isError
    }
  }

  if (enabledConfig === undefined) {
    return { state: null, isLoading, isError }
  }

  const configValue = (name: ConfigName): unknown =>
    results[configNames.indexOf(name)]?.data ?? null

  return {
    state: {
      isEnabled: configValue(enabledConfig) === true,
      isConfigured: requiredConfigKeys.every((name) => {
        const value = configValue(name)
        return value !== null && value !== ''
      })
    },
    isLoading,
    isError
  }
}
